import { Head, Link, router, useForm } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import Table, { Th, Td, Tr } from '@/Components/Table';
import PageHeader from '@/Components/PageHeader';
import EmptyState from '@/Components/EmptyState';
import FormInput from '@/Components/FormInput';
import FormSelect from '@/Components/FormSelect';
import Alert from '@/Components/Alert';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import { EnvelopeIcon, TrashIcon, ArrowPathIcon, PaperAirplaneIcon, ArrowLeftIcon, MagnifyingGlassIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

export default function Index({ invitations, filters, flash }) {
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [search, setSearch] = useState(filters?.search || '');
    const [status, setStatus] = useState(filters?.status || '');
    const [sendResult, setSendResult] = useState(null);

    const sendForm = useForm({ email: '' });

    const handleSend = (e) => {
        e.preventDefault();
        setSendResult(null);
        sendForm.post('/supplier-invitations', {
            preserveScroll: true,
            onSuccess: () => {
                sendForm.reset();
            },
        });
    };

    const handleResend = (id) => {
        router.post(`/supplier-invitations/${id}/resend`, {}, { preserveScroll: true });
    };

    const handleDelete = (id) => {
        router.delete(`/supplier-invitations/${id}`, { preserveScroll: true });
        setDeleteConfirm(null);
    };

    const applyFilters = (e) => {
        e?.preventDefault();
        router.get('/supplier-invitations', { search, status }, { preserveState: true });
    };

    const statusBadge = (s) => {
        const map = { sent: 'info', pending: 'warning', completed: 'success', opened: 'info', expired: 'neutral' };
        return <Badge variant={map[s] || 'neutral'}>{s || 'Unknown'}</Badge>;
    };

    return (
        <AppLayout>
            <Head title="Supplier Invitations" />
            <PageHeader title="Supplier Invitations" subtitle="Send and manage supplier registration invitations" actions={
                <Link href="/suppliers" className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors"><ArrowLeftIcon className="w-4 h-4" /> Back to Suppliers</Link>
            } />

            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}
            {flash?.warning && <Alert type="warning" className="mb-4">{flash.warning}</Alert>}

            {/* Send Invitation Form */}
            <Card className="mb-6">
                <CardBody>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Send New Invitation</h3>
                    <form onSubmit={handleSend} className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1">
                            <FormInput
                                type="email"
                                placeholder="Enter supplier email address..."
                                value={sendForm.data.email}
                                onChange={e => sendForm.setData('email', e.target.value)}
                                error={sendForm.errors.email}
                                required
                            />
                        </div>
                        <Button type="submit" disabled={sendForm.processing} size="sm">
                            <PaperAirplaneIcon className="w-4 h-4" />
                            {sendForm.processing ? 'Sending...' : 'Send Invitation'}
                        </Button>
                    </form>
                </CardBody>
            </Card>

            {/* Filters */}
            <Card className="mb-6">
                <CardBody>
                    <form onSubmit={applyFilters} className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input type="text" placeholder="Search by email..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20" />
                        </div>
                        <FormSelect value={status} onChange={e => setStatus(e.target.value)} className="w-full sm:w-40">
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="sent">Sent</option>
                            <option value="opened">Opened</option>
                            <option value="completed">Completed</option>
                        </FormSelect>
                        <div className="flex gap-2">
                            <Button type="submit" size="sm">Apply</Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => { setSearch(''); setStatus(''); router.get('/supplier-invitations'); }}>Clear</Button>
                        </div>
                    </form>
                </CardBody>
            </Card>

            {/* Invitations Table */}
            <Card>
                <Table minWidth="600px">
                    <thead>
                        <tr><Th>Email</Th><Th>Status</Th><Th>Sent</Th><Th>Expires</Th><Th>Actions</Th></tr>
                    </thead>
                    <tbody>
                        {(Array.isArray(invitations) ? invitations : []).map(inv => (
                            <Tr key={inv.id}>
                                <Td className="font-medium text-slate-800">{inv.email}</Td>
                                <Td>{statusBadge(inv.status)}</Td>
                                <Td className="text-xs text-slate-500">{inv.sent_at ? new Date(inv.sent_at).toLocaleDateString() : '-'}</Td>
                                <Td className="text-xs text-slate-500">{inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : '-'}</Td>
                                <Td>
                                    <div className="flex items-center gap-1">
                                        {inv.status !== 'completed' && (
                                            <button onClick={() => handleResend(inv.id)} className="p-1.5 hover:bg-blue-50 rounded-lg" title="Resend">
                                                <ArrowPathIcon className="w-4 h-4 text-blue-500" />
                                            </button>
                                        )}
                                        {inv.status !== 'completed' && (
                                            <button onClick={() => setDeleteConfirm(inv.id)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Delete">
                                                <TrashIcon className="w-4 h-4 text-red-400" />
                                            </button>
                                        )}
                                        {inv.status === 'completed' && (
                                            <span className="text-xs text-emerald-600 font-medium">Registered</span>
                                        )}
                                    </div>
                                </Td>
                            </Tr>
                        ))}
                    </tbody>
                </Table>
                {(!invitations || invitations.length === 0) && (
                    <EmptyState icon={EnvelopeIcon} title="No invitations" description="Send an invitation to get started." />
                )}
            </Card>

            <Modal show={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Delete Invitation" iconBg="bg-red-100" iconColor="text-red-600" icon={<TrashIcon className="w-5 h-5" />}>
                <ModalBody><p className="text-sm text-slate-600">Are you sure you want to delete this invitation?</p></ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button variant="danger" onClick={() => handleDelete(deleteConfirm)}>Delete</Button>
                </ModalFooter>
            </Modal>
        </AppLayout>
    );
}
